import React, { useEffect, useState } from "react";
import GroupedStackedBarChart from "../../../CHARTS/GroupedStackedBarChart";

const StackedShiftWiseData = ({ data, from, subCauseWiseChart }) => {
  const [chartData, setChartData] = useState(null);
  const [chartDataYearWise, setChartDataYearWise] = useState(null);

  const transformYearlyData = (inputData) => {
    if (!inputData || !Array.isArray(inputData) || inputData.length === 0)
      return {};

    // Step 1: Collect all categories and data grouped by year and shift
    const categories = new Set();
    const groupsData = {};
    const categoryTotals = {};

    inputData.forEach((year) => {
      const yearKey = year.F_YEAR;
      groupsData[yearKey] = {};

      year.children.forEach((assetGroup) => {
        assetGroup.children.forEach((mainCause) => {
          mainCause.children.forEach((subCause) => {
            const category = subCause.SUB_CAUSE_DESC;
            categories.add(category);

            if (!categoryTotals[category]) {
              categoryTotals[category] = 0;
            }

            subCause.children.forEach((month) => {
              month.data.forEach((entry) => {
                const shift = entry.SHIFT;

                if (!groupsData[yearKey][shift]) {
                  groupsData[yearKey][shift] = {};
                }

                if (!groupsData[yearKey][shift][category]) {
                  groupsData[yearKey][shift][category] = 0;
                }

                groupsData[yearKey][shift][category] += 1;
                categoryTotals[category] += 1;
              });
            });
          });
        });
      });
    });

    // Step 2: Sort categories by total value in descending order
    const sortedCategories = Array.from(categories).sort(
      (a, b) => categoryTotals[b] - categoryTotals[a]
    );

    // Step 3: Format categories with the year range
    const yearRange = Object.keys(groupsData).sort().join(" - ");
    const categoriesArray = sortedCategories.map(
      (category) => `${yearRange}\n${category}`
    );

    // Step 4: Define the desired order of shifts
    const shiftOrder = ["00_08", "08_16", "16_24"];

    // Step 5: Transform groupsData into the desired groups structure with sorted years and ordered shifts
    const groups = Object.keys(groupsData)
      .sort() // Sort years in ascending order (oldest first)
      .map((yearKey) => {
        const stacks = shiftOrder.map((shift) => ({
          name: shift,
          data: sortedCategories.map(
            (category) => groupsData[yearKey][shift]?.[category] || 0 // Use 0 if shift doesn't exist
          ),
        }));

        return {
          name: yearKey,
          stacks,
        };
      });

    // Step 6: Construct the final result
    return {
      categories: categoriesArray,
      groups,
    };
  };

  const transformYearlyDataYearWise = (inputData) => {
    if (!inputData || !Array.isArray(inputData) || inputData.length === 0) {
      return { categories: [], groups: [] };
    }

    // Step 1: Aggregate data by Year → Sub Cause (sum across all shifts)
    const yearData = {}; // { "2023-24": { "Bearing Failure": 25, "Overload": 18, ... } }
    const categoryTotals = {}; // For sorting: { "Bearing Failure": 120, ... }

    inputData.forEach((year) => {
      const yearKey = year.F_YEAR;

      if (!yearData[yearKey]) {
        yearData[yearKey] = {};
      }

      year.children.forEach((assetGroup) => {
        assetGroup.children.forEach((mainCause) => {
          mainCause.children.forEach((subCause) => {
            const category = subCause.SUB_CAUSE_DESC;

            // Initialize counters
            if (!yearData[yearKey][category]) yearData[yearKey][category] = 0;
            if (!categoryTotals[category]) categoryTotals[category] = 0;

            // Loop through months and entries, count each occurrence (regardless of shift)
            subCause.children.forEach((month) => {
              month.data.forEach((entry) => {
                yearData[yearKey][category] += 1;
                categoryTotals[category] += 1;
              });
            });
          });
        });
      });
    });

    // Step 2: Sort sub-causes by total count (descending)
    const sortedCategories = Object.keys(categoryTotals).sort(
      (a, b) => categoryTotals[b] - categoryTotals[a]
    );

    // Step 3: Create category labels with full year range
    const years = Object.keys(yearData).sort();
    const yearRange = years.length > 0 ? years.join(" - ") : "";
    const categories = sortedCategories.map((cat) => `${yearRange}\n${cat}`);

    // Step 4: Build groups — one per year, single stack "Total"
    const groups = years.map((yearKey) => ({
      name: yearKey,
      stacks: [
        {
          name: "Total", // You can also use yearKey or "All Shifts"
          data: sortedCategories.map((cat) => yearData[yearKey][cat] || 0),
        },
      ],
    }));

    // Final result
    return {
      categories,
      groups,
    };
  };

  useEffect(() => {
    let temp = transformYearlyData(data);
    setChartData(temp);
  }, [data]);
  useEffect(() => {
    let temp = transformYearlyDataYearWise(data);
    setChartDataYearWise(temp);
  }, [data]);

  return (
    <div className="papaDiv" style={{ background: "rgba(0,0,0,0)" }}>
      {from === "Sub Cause" || (
        <h6 className="bg-dark text-white p-1">
          {from === "asset" ? "Head" : from} wise cause
        </h6>
      )}
      {subCauseWiseChart && chartData && chartData?.groups?.length > 1 && (
        <GroupedStackedBarChart data={chartData} />
      )}{" "}
      {!subCauseWiseChart &&
        chartDataYearWise &&
        chartDataYearWise?.groups?.length > 1 && (
          <GroupedStackedBarChart data={chartDataYearWise} />
        )}
    </div>
  );
};

export default StackedShiftWiseData;
